/**
 * Graphics on a photo, as the parts a designer would draw them with — the
 * backoffice side of pms-api-v2's studio-graphics.ts (keep the two in step).
 *
 * A callout is a TEXT, a RECT wrapping it, a LINE from the rect and a DOT where
 * it points. Positions are fractions of the frame, 0..1 from the top-left;
 * parts refer to each other by id, and the geometry that has to agree (a box
 * around its words, a line leaving the side of a box) is worked out when they
 * are drawn.
 */

/** The typefaces parts can be set in (the API's TYPE_FACES; the video editor's list). */
export const TYPE_FACES = [
    'Cormorant Garamond', 'Playfair Display', 'Bodoni Moda', 'DM Serif Display', 'Libre Baskerville', 'Fraunces',
    'Allura', 'Pinyon Script',
    'Inter', 'Manrope', 'Montserrat', 'Poppins', 'Syne', 'Space Grotesk',
    'Bebas Neue', 'Archivo Black', 'Italiana',
] as const;
export type PartAnim = 'none' | 'fade' | 'rise' | 'mask-up' | 'typewriter' | 'letter-stagger' | 'word-stagger' | 'tracking-in' | 'blur' | 'scale' | 'wipe';
/** When a part shows in a film, relative to its shot (photos ignore it). */
export interface GraphicTiming { at?: number; dur?: number; anim?: PartAnim; out?: PartAnim; }
interface GraphicBase extends GraphicTiming { id?: string; group?: string; color?: string; }
export interface GraphicText extends GraphicBase { type: 'text'; text: string; x: number; y: number; align?: 'left' | 'center' | 'right'; size?: number; font?: 'serif' | 'sans' | 'mono'; family?: string; weight?: number; upper?: boolean; italic?: boolean; spacing?: number; outline?: boolean; }
/** The vendor's logo, where the reference had one. x, y = its centre; w = width as a fraction of the frame. */
export interface GraphicLogo extends GraphicBase { type: 'logo'; x: number; y: number; w?: number; }
export interface GraphicRect extends GraphicBase { type: 'rect'; wrap?: string; pad?: number; x?: number; y?: number; w?: number; h?: number; stroke?: number; fill?: string; radius?: number; breaks?: string[]; }
export interface GraphicLine extends GraphicBase { type: 'line'; from?: string; x1?: number; y1?: number; to?: string; x2?: number; y2?: number; stroke?: number; dash?: boolean; }
export interface GraphicDot extends GraphicBase { type: 'dot'; x: number; y: number; r?: number; ring?: boolean; }
export type Graphic = GraphicText | GraphicRect | GraphicLine | GraphicDot | GraphicLogo;

/** The parts of one callout: words, the box around them, a dot on the point, a line from box to dot. */
export function calloutParts(n: number, c: { text: string; x: number; y: number; labelX?: number; labelY?: number }): Graphic[] {
    const group = `Callout ${n}`;
    const lx = c.labelX ?? (c.x > 0.5 ? c.x - 0.28 : c.x + 0.28);
    const ly = c.labelY ?? c.y;
    return [
        { type: 'text', id: `c${n}t`, group, text: c.text, x: lx, y: ly, align: 'center', size: 0.018, font: 'sans', upper: true, spacing: 160 },
        { type: 'rect', id: `c${n}b`, group, wrap: `c${n}t`, pad: 0.011, stroke: 1.5, fill: 'rgba(0,0,0,0.12)' },
        { type: 'dot', id: `c${n}d`, group, x: c.x, y: c.y, r: 0.006 },
        { type: 'line', group, from: `c${n}b`, to: `c${n}d`, stroke: 1.5 },
    ];
}

/** The older callout shorthand, read as parts. */
export function calloutsToGraphics(callouts: unknown): Graphic[] | undefined {
    if (!Array.isArray(callouts) || !callouts.length) return undefined;
    const out = callouts
        .filter((c: any) => c && String(c.text || '').trim() && Number.isFinite(c.x) && Number.isFinite(c.y))
        .flatMap((c: any, i: number) => calloutParts(i + 1, c));
    return out.length ? out : undefined;
}

/** A shot's graphics, whichever way they were written. */
export function graphicsOf(shot: { graphics?: Graphic[]; callouts?: unknown }): Graphic[] {
    return shot.graphics?.length ? shot.graphics : calloutsToGraphics(shot.callouts) || [];
}

/** The next free callout number in a list of parts. */
export function nextCalloutNumber(graphics: Graphic[]): number {
    let n = 0;
    for (const g of graphics) {
        const m = /^Callout (\d+)$/.exec(g.group || '');
        if (m) n = Math.max(n, Number(m[1]));
    }
    return n + 1;
}
